import type { CaptureReason } from '@contracts/common';

/** What a failed recording says, by why it failed (2026-10-08). */
export interface WhyNothing {
  /** The heading on the meeting's own page. */
  title: string;
  /** One line under a row in a list. */
  line: string;
  /** The pill on that row. */
  pill: string;
  /** The longer telling, for "What happened". */
  story: (title: string) => string;
}

/**
 * Said for each reason, never the one sentence for all of them: every failed
 * meeting used to read "nobody let the notetaker in", including the lunches it
 * sat in alone (Aleksandr: "recorded 5:31, but nothing in it").
 */
export function whyNothing(reason: CaptureReason | null): WhyNothing {
  switch (reason) {
    case 'NOBODY_CAME':
      return {
        title: 'Nobody came.',
        line: 'the notetaker waited five minutes alone and left — nothing was recorded',
        pill: 'Nobody came',
        story: (t) =>
          `The notetaker joined “${t}”, waited five minutes with nobody else in the call, and left. Nothing was recorded.`,
      };
    case 'REMOVED':
      return {
        title: 'The notetaker was removed from the call.',
        line: 'somebody removed the notetaker — nothing was recorded',
        pill: 'Removed',
        story: (t) =>
          `Somebody removed the notetaker from “${t}” before anything was recorded. There is no transcript and no note for it.`,
      };
    case 'NO_RECORDING':
      return {
        title: 'Nothing was recorded.',
        line: 'the notetaker was in the call, but no recording came of it',
        pill: 'Not recorded',
        story: (t) =>
          `The notetaker was in “${t}”, but no recording came of it — there is no transcript and no note for that hour.`,
      };
    default:
      // NOT_LET_IN, and every failure from before the reason was kept.
      return {
        title: 'Nobody let the notetaker in.',
        line: 'waited five minutes in the lobby, nobody let it in — no transcript exists',
        pill: 'Couldn’t get in',
        story: (t) =>
          `The notetaker knocked at “${t}” and nobody let it in within five minutes, so it left. There is no transcript and no note for that hour.`,
      };
  }
}
